import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { useNavigate } from 'react-router-dom';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
const ProfileIncompleteModal = ({ visible, onHide, feature }) => {
    const navigate = useNavigate();
    const [showDetails, setShowDetails] = useState(false);
    
    // Use the profile completion hook for real-time data
    const { profileCompletion, loading } = useProfileCompletion();
    
    // Fallback if no profile data
    const safeProfileCompletion = profileCompletion || { percentage: 0, isComplete: false, sectionStatus: {} };

    const getSeverity = (percentage) => {
        if (percentage >= 75) return 'info';
        if (percentage >= 50) return 'warning';
        return 'danger';
    };

    const getFeatureName = (feature) => {
        const featureNames = {
            'equifax': 'Equifax Report',
            'experian': 'Experian Report',
            'bank-statement': 'Bank Statement Analysis',
            'pan-verification': 'PAN Verification',
            'employment-history': 'Employment History',
            'driving-licence': 'Driving Licence Verification',
            'voter-id': 'Voter ID Validation',
            'mobile-bureau': 'Mobile Bureau Lookup',
            'ca-membership': 'CA Membership Verification',
            'credits': 'Credit Wallet',
            'shared-leads': 'Shared Leads'
        };
        return featureNames[feature] || 'This feature';
    };

    const handleCompleteProfile = () => {
        onHide();
        navigate('/app/profile/complete');
    };

    const footer = (
        <div className="flex justify-content-between align-items-center w-full">
            <Button 
                label="Not Now" 
                className="p-button-text" 
                onClick={onHide}
            />
            <Button 
                label="Complete Profile" 
                icon="pi pi-arrow-right" 
                iconPos="right"
                onClick={handleCompleteProfile}
                severity="success"
            />
        </div>
    );

    const getMissingSections = () => {
        const sections = [];
        if (!safeProfileCompletion.sectionStatus?.basic) sections.push('Basic Information');
        if (!safeProfileCompletion.sectionStatus?.userType) sections.push('User Type');
        if (!safeProfileCompletion.sectionStatus?.establishment) sections.push('Establishment Details');
        if (!safeProfileCompletion.sectionStatus?.banking) sections.push('Banking Information');
        return sections;
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-lock text-2xl text-orange-500"></i>
                    <span>Complete Your Profile to Continue</span>
                </div>
            }
            footer={footer}
            modal
            className="profile-incomplete-modal"
            style={{ width: '500px' }}
            breakpoints={{ '768px': '90vw' }}
        >
            <div className="p-3">
                {/* Main Message */}
                <Message 
                    severity="warning" 
                    text={`${getFeatureName(feature)} requires a complete profile for security and compliance purposes.`}
                    className="mb-3 w-full"
                />

                {/* Progress Section */}
                <div className="mb-4">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="font-semibold">Profile Completion Status</span>
                        <Tag 
                            value={`${safeProfileCompletion.percentage}%`} 
                            severity={getSeverity(safeProfileCompletion.percentage)}
                        />
                    </div>
                    <ProgressBar 
                        value={safeProfileCompletion.percentage} 
                        showValue={false}
                        style={{ height: '8px' }}
                        color={safeProfileCompletion.percentage < 50 ? '#ef4444' : safeProfileCompletion.percentage < 75 ? '#f59e0b' : '#3b82f6'}
                     aria-label="Progress" />
                </div>

                {/* Missing Sections */}
                {getMissingSections().length > 0 && (
                    <div className="mb-3">
                        <div className="font-semibold mb-2 text-sm">
                            <i className="pi pi-info-circle mr-1"></i>
                            Incomplete Sections:
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {getMissingSections().map((section, index) => (
                                <Tag 
                                    key={index} 
                                    value={section} 
                                    severity="danger"
                                    className="text-xs"
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Benefits of Completing Profile */}
                <div className="surface-100 border-round p-3">
                    <div className="font-semibold mb-2 text-sm">
                        <i className="pi pi-check-circle mr-1 text-green-500"></i>
                        Why Complete Your Profile?
                    </div>
                    <ul className="m-0 pl-3 text-sm">
                        <li className="mb-1">Access all verification services</li>
                        <li className="mb-1">Faster processing of requests</li>
                        <li className="mb-1">Enhanced security and compliance</li>
                        <li>Priority customer support</li>
                    </ul>
                </div>

                {/* Quick Tips */}
                <div className="mt-3 text-center text-sm text-600">
                    <i className="pi pi-clock mr-1"></i>
                    Takes only 2-3 minutes to complete
                </div>
            </div>
        </Dialog>
    );
};

export default ProfileIncompleteModal;